import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CalendarDay, type CalendarYear } from '@/api/client'
import { Banner, ErrorNote, Spinner, fmtDate, yearNow } from '@/components/ui'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const ORDINALS = [1, 2, 3, 4, 5]

/** The 12-month grid is what makes the pattern verifiable: set Saturdays to 2,4
 *  and you watch the year repaint, rather than discovering it in March.
 *
 *  **The grid is also how a day is opened or closed.** "Today is Friday but we are
 *  working this weekend" is a real thing an Owner needs to say at 4pm, and it used to
 *  mean leaving this screen for Master data and typing two dates. Clicking the day you
 *  are already looking at is the same decision with the verification built in - the
 *  cell you clicked repaints, so the change is confirmed by the glance that made it.
 *
 *  A click asks rather than toggles. Silently changing which days a firm works, on a
 *  single mis-click, is not a thing to be casual about, and the panel is where the
 *  consequence gets stated. */
export function CalendarPage() {
  const qc = useQueryClient()
  const [year, setYear] = useState(yearNow())
  const [err, setErr] = useState<unknown>(null)
  const [picked, setPicked] = useState<CalendarDay | null>(null)
  const [label, setLabel] = useState('')
  const cal = useQuery({
    queryKey: ['calendar', year],
    queryFn: () => api.get<CalendarYear>(`/calendar?year=${year}`),
  })
  const patch = useMutation({
    mutationFn: (body: Record<string, string>) => api.patch('/settings', body),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['calendar'] }) },
    onError: setErr,
  })

  // The same three keys `HolidaysSection` invalidates: the grid, the master list, and
  // today's banner, which says whether the firm is working right now.
  const settled = () => {
    setErr(null)
    setPicked(null)
    setLabel('')
    qc.invalidateQueries({ queryKey: ['calendar'] })
    qc.invalidateQueries({ queryKey: ['holidays'] })
    qc.invalidateQueries({ queryKey: ['day'] })
  }
  const mark = useMutation({
    mutationFn: (body: { date: string; name: string; type: string }) =>
      api.post('/holidays', body),
    onSuccess: settled,
    onError: setErr,
  })
  const clear = useMutation({
    mutationFn: (date: string) => api.del(`/holidays/${date}`),
    onSuccess: settled,
    onError: setErr,
  })
  const busy = mark.isPending || clear.isPending

  if (cal.isLoading) return <Spinner label="Building the calendar" />
  if (cal.error) return <ErrorNote error={cal.error} />
  const c = cal.data!

  const satSet = c.saturday_working_ordinals.trim().toUpperCase()
  const satAll = satSet === 'ALL'
  const satOn = new Set(satAll ? ORDINALS : satSet.split(',').map(Number).filter(Boolean))

  const setSat = (o: number) => {
    const next = new Set(satOn)
    next.has(o) ? next.delete(o) : next.add(o)
    const all = ORDINALS.every((x) => next.has(x))
    patch.mutate({ saturday_working_ordinals: all ? 'ALL' : [...next].sort().join(',') })
  }

  const byMonth = MONTHS.map((_, m) =>
    c.days.filter((d) => Number(d.date.slice(5, 7)) === m + 1))

  const cell = (kind: string, working: boolean) =>
    kind === 'PUBLIC' || kind === 'OFFICE_CLOSED' ? 'bg-rej-tint text-rej-ink'
    : kind === 'WORKING_DAY' ? 'bg-done-tint text-done-ink font-semibold'
    : working ? 'bg-surface text-ink' : 'bg-rule-2 text-ink-3'

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      {c.warning && <Banner tone="warn"><b>{c.warning}</b> An unloaded year does not
        error — it silently treats holidays as ordinary working days and carries tasks
        onto them.</Banner>}

      <div className="card mb-4 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="label">Working weekdays</label>
            <div className="mono text-[13px]">{c.working_days}</div>
          </div>
          <div>
            <label className="label">Saturdays worked</label>
            <div className="flex gap-1">
              {ORDINALS.map((o) => (
                <button key={o} type="button" onClick={() => setSat(o)} disabled={patch.isPending}
                        className={`h-7 w-9 rounded border text-xs font-medium
                          ${satOn.has(o) ? 'bg-primary border-primary text-primary-on'
                                         : 'bg-surface border-rule text-ink-3'}`}>
                  {o}
                  <sup>{o === 1 ? 'st' : o === 2 ? 'nd' : o === 3 ? 'rd' : 'th'}</sup>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Sundays worked</label>
            <div className="mono text-[13px] text-ink-3">
              {c.sunday_working_ordinals.trim() || 'never'}
            </div>
          </div>
          <div>
            <label className="label">Calendar good through</label>
            <div className="mono text-[13px]">{c.configured_through || '—'}</div>
          </div>
          <span className="flex-1" />
          <div className="flex items-center gap-2">
            <button className="btn btn-sm" onClick={() => setYear(year - 1)}>←</button>
            <span className="mono text-[15px] font-semibold w-14 text-center">{year}</span>
            <button className="btn btn-sm" onClick={() => setYear(year + 1)}>→</button>
          </div>
        </div>
        <div className="flex gap-4 mt-3.5 text-[11.5px] text-ink-2">
          {[['bg-surface border border-rule', 'working'], ['bg-rule-2', 'weekend off'],
            ['bg-rej-tint', 'holiday'], ['bg-done-tint', 'override — working']].map(([cls, label]) => (
            <span key={label} className="flex items-center gap-1.5">
              <i className={`h-2.5 w-2.5 rounded-sm ${cls}`} /> {label}
            </span>
          ))}
          <span className="flex-1" />
          <span className="text-ink-3">
            Click any day to open or close it — the weekday pattern above is the rule,
            a click is the exception.
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {byMonth.map((days, m) => (
          <div key={m} className="card p-2.5">
            <div className="text-[12.5px] font-semibold mb-1.5">{MONTHS[m]}</div>
            <div className="grid grid-cols-7 gap-[2px]">
              {['M','T','W','T','F','S','S'].map((d, i) => (
                <div key={i} className="text-center text-[9px] text-ink-3">{d}</div>
              ))}
              {days.length > 0 && Array.from(
                { length: (new Date(days[0].date + 'T00:00:00').getDay() + 6) % 7 },
                (_, i) => <div key={`pad${i}`} />)}
              {days.map((d) => (
                <button key={d.date} type="button" disabled={busy}
                        title={`${d.date}${d.name ? ' · ' + d.name : ''} — click to change`}
                        onClick={() => { setPicked(d); setLabel(d.name) }}
                        className={`text-center text-[10px] rounded-sm py-[3px]
                          hover:ring-1 hover:ring-primary focus:outline-none
                          focus:ring-1 focus:ring-primary
                          ${picked?.date === d.date ? 'ring-1 ring-primary' : ''}
                          ${cell(d.kind, d.working)}`}>
                  {Number(d.date.slice(8, 10))}
                </button>
              ))}
            </div>
            {picked && Number(picked.date.slice(5, 7)) === m + 1 && (
              <DayPanel day={picked} label={label} setLabel={setLabel} busy={busy}
                        onClose={() => setPicked(null)}
                        onMark={(type, name) =>
                          mark.mutate({ date: picked.date, name, type })}
                        onClear={() => clear.mutate(picked.date)} />
            )}
          </div>
        ))}
      </div>
    </>
  )
}


/** What one day can be changed into, stated rather than toggled.
 *
 *  Rendered inside its own month's card, so nothing has to be positioned and it works
 *  on a phone unchanged. The grid answers "does the firm work this day"; the named
 *  list of holidays is still managed on Master data → Holidays, which is where a
 *  holiday gets deleted outright.
 */
function DayPanel({ day, label, setLabel, busy, onClose, onMark, onClear }: {
  day: CalendarDay
  label: string
  setLabel: (v: string) => void
  busy: boolean
  onClose: () => void
  onMark: (type: string, name: string) => void
  onClear: () => void
}) {
  // A row exists for this day when the calendar named a kind rather than deriving it
  // from the weekday pattern.
  const hasRow = ['PUBLIC', 'OFFICE_CLOSED', 'WORKING_DAY'].includes(day.kind)
  const named = label.trim()

  return (
    <div className="mt-2 border-t border-rule pt-2">
      <div className="flex items-baseline gap-1.5">
        <b className="text-[11.5px]">{fmtDate(day.date)}</b>
        <span className="text-[10.5px] text-ink-3">
          {day.working ? 'working' : 'not working'}
        </span>
        <span className="flex-1" />
        <button className="text-ink-3 hover:text-ink text-[13px] px-1"
                onClick={onClose}>×</button>
      </div>

      <input className="field mt-1.5 text-[11.5px] py-1" value={label} maxLength={60}
             placeholder={day.working ? 'Why is it closed? e.g. Diwali'
                                      : 'Why is it open? e.g. GST deadline'}
             onChange={(e) => setLabel(e.target.value)} />

      <div className="flex flex-col gap-1 mt-1.5">
        {day.working ? (
          <button className="btn btn-sm" disabled={busy}
                  onClick={() => onMark('OFFICE_CLOSED', named || 'Office closed')}>
            Close this day
          </button>
        ) : (
          <button className="btn btn-sm btn-primary" disabled={busy}
                  onClick={() => onMark('WORKING_DAY', named || 'Working day')}>
            Work this day
          </button>
        )}
        {hasRow && (
          <button className="btn btn-sm" disabled={busy} onClick={onClear}>
            Back to the usual pattern
          </button>
        )}
      </div>

      <p className="mt-1.5 text-[10.5px] text-ink-3 leading-snug">
        Work already carried onto another day stays there — this decides where work
        goes from tonight, not where it has been.
      </p>
    </div>
  )
}
